import Vector from "victor";
import throttle from "lodash.throttle";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeDOMRepresentation } from "../types/ShapeDOMRepresentation";
import Rect from "../types/Rect";
import { Verb } from "../types/Verb";
import { Scope } from "../types/Scope";
import Camera from "../Camera";
import { ShapeTranslatable2 } from "../types/Shape";
import { TranslatableSubject } from "../operations/MoveOperation";
import { SubjectMessageAny } from "../ShapeStore";
import {
  AttributeKey,
  HasFillColor,
  HasStrokeColor,
  HasStrokeWidth,
  HasTranslate,
} from "../types/attributes";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import { DragHandle, DragHandlePointA, DragHandlePointB } from "./DragHandle";
import { WBHUDState } from "@/store/WBHUDState";
import { Operation } from "../operations/Operation";
import { SetAttributeOperation } from "../operations/SetAttributeOperation";
import { TextMaxWidthDragHandle } from "./TextMaxWidthDragHandle";
import { NodeShape } from "../standardShapes/NodeShape";

type LodashThrottledFunction = {
  cancel: () => void;
} & ((scope: Scope, point: Vector) => void);

export interface SelectionDelegate {
  selectionDidRequestDelete: (selection: Selection) => void;
}

export class Selection {
  shapeDR: ShapeDOMRepresentation;
  el: HTMLDivElement;
  originalTranslate = new Vector(0, 0);
  wb: WhiteboardControllerInterface;
  throttledApplyTranslate: LodashThrottledFunction;
  delegate: SelectionDelegate;
  dragHandles: DragHandle[] = [];

  constructor(
    delegate: SelectionDelegate,
    wb: WhiteboardControllerInterface,
    shapeDR: ShapeDOMRepresentation
  ) {
    this.delegate = delegate;
    this.wb = wb;
    this.shapeDR = shapeDR;
    this.el = document.createElement("div");
    this.el.className = "WBSelectionRect";

    this.throttledApplyTranslate = throttle(this._applyTranslate, 300, {
      leading: true,
    });

    const addAB = (shouldApplyConstraints = true) => {
      this._addDragHandle(
        new DragHandlePointA(this.shapeDR, shouldApplyConstraints)
      );
      this._addDragHandle(
        new DragHandlePointB(this.shapeDR, shouldApplyConstraints)
      );
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const addCorners = (shouldApplyConstraints = true) => {
      return;
      // this._addDragHandle(
      //   new DragHandlePointAOrB(this.shapeDR, true, shouldApplyConstraints)
      // );
      // this._addDragHandle(
      //   new DragHandlePointAOrB(this.shapeDR, false, shouldApplyConstraints)
      // );
    };

    switch (this.shapeDR.shape.shapeType) {
      case ShapeType.Line:
        addAB(false);
        break;
      case ShapeType.Rect:
      case ShapeType.Ellipse:
      case ShapeType.Diamond:
        addAB(false);
        addCorners(false);
        break;
      case ShapeType.Node:
        addAB();
        break;
      case ShapeType.Pen:
        break; // no handles
      case ShapeType.Text:
        this._addDragHandle(new TextMaxWidthDragHandle(this.shapeDR));
        break;
    }
  }

  private _addDragHandle(h: DragHandle) {
    this.dragHandles.push(h);
    this.el.appendChild(h.el);
  }

  didMount() {
    this.wb.shapeStore.changes.onValue(this.respondToEvent);
  }

  willUnmount() {
    this.wb.shapeStore.changes.offValue(this.respondToEvent);
    for (const h of this.dragHandles) {
      h.teardown();
    }
  }

  settingsDidChange(settings: WBHUDState): Operation[] {
    const operations = new Array<Operation>();
    // stroke color
    if (settings.changedKeys.indexOf("strokeColor") !== -1) {
      switch (this.shapeDR.shape.shapeType) {
        case ShapeType.Line:
        case ShapeType.Diamond:
        case ShapeType.Ellipse:
        case ShapeType.Rect:
        case ShapeType.Pen:
          operations.push(
            new SetAttributeOperation(
              [this.shapeDR.id],
              AttributeKey.StrokeColor,
              (this.shapeDR.shape as unknown as HasStrokeColor).strokeColor,
              settings.strokeColor
            )
          );
          break;
        case ShapeType.Text:
        case ShapeType.Node:
          break; // stroke color doesn't apply the same way
      }
    }

    // stroke width
    if (settings.changedKeys.indexOf("strokeWidth") !== -1) {
      switch (this.shapeDR.shape.shapeType) {
        case ShapeType.Line:
        case ShapeType.Diamond:
        case ShapeType.Ellipse:
        case ShapeType.Rect:
        case ShapeType.Pen:
          operations.push(
            new SetAttributeOperation(
              [this.shapeDR.id],
              AttributeKey.StrokeWidth,
              (this.shapeDR.shape as unknown as HasStrokeWidth).strokeWidth,
              settings.strokeWidth
            )
          );
          break;
        case ShapeType.Text:
        case ShapeType.Node:
          break; // stroke color doesn't apply the same way
      }
    }

    // fill color
    if (settings.changedKeys.indexOf("fillColor") !== -1) {
      switch (this.shapeDR.shape.shapeType) {
        case ShapeType.Diamond:
        case ShapeType.Ellipse:
        case ShapeType.Rect:
          operations.push(
            new SetAttributeOperation(
              [this.shapeDR.id],
              AttributeKey.FillColor,
              (this.shapeDR.shape as unknown as HasFillColor).fillColor,
              settings.fillColor
            )
          );
          break;
        case ShapeType.Line:
        case ShapeType.Pen:
        case ShapeType.Text:
        case ShapeType.Node:
          break; // no fill
      }
    }

    // fill color (sticky)
    if (settings.changedKeys.indexOf("fillColorForSticky") !== -1) {
      switch (this.shapeDR.shape.shapeType) {
        case ShapeType.Diamond:
        case ShapeType.Ellipse:
        case ShapeType.Line:
        case ShapeType.Pen:
        case ShapeType.Text:
          break;
        case ShapeType.Node:
          operations.push(
            new SetAttributeOperation(
              [this.shapeDR.id],
              AttributeKey.FillColor,
              (this.shapeDR.shape as NodeShape).fillColor,
              settings.fillColorForSticky
            )
          );
          break;
      }
    }

    return operations;
  }

  // Camera settings have changed, adjust accordingly
  respondToEvent = (e: SubjectMessageAny) => {
    if (e.subjectID !== this.shapeDR.id) return;
    this.updateTransform(this.wb.camera);
  };

  // Convert shape to TranslatableSubject
  get translatableSubject(): TranslatableSubject {
    return {
      subjectID: this.shapeDR.id,
      translate: (this.shapeDR.shape as unknown as HasTranslate).translate,
    };
  }

  handleDelete = (e: MouseEvent) => {
    this.delegate.selectionDidRequestDelete(this);
    e.stopPropagation();
  };

  // SelectionTool uses this to decide what to do on mousedown
  getDragHandle(screenPoint: Vector): DragHandle | null {
    for (const h of this.dragHandles) {
      if (h.getIsPointOnHandle(screenPoint)) return h;
    }
    return null;
  }

  // Drag operation is starting; remember where we were
  setOriginalTranslate() {
    const shapeTranslatable = this.shapeDR.shape as ShapeTranslatable2;
    if (!shapeTranslatable) return;
    this.originalTranslate = Vector.fromArray(shapeTranslatable.translate);
  }

  // Drag operation is done, but we need to reset this value so
  // we can accurately create an undoable operation
  resetToOriginalTranslate() {
    const shapeTranslatable = this.shapeDR.shape as ShapeTranslatable2;
    if (!shapeTranslatable) return;
    shapeTranslatable.translate = this.originalTranslate.toArray() as [
      number,
      number
    ];
  }

  // Send an event that updates this shape's translate property
  _applyTranslate(scope: Scope, worldDelta: Vector) {
    const newTranslate = this.originalTranslate.clone().add(worldDelta);
    this.wb.shapeStore.send(scope, {
      verb: Verb.UpsertSubjectAttributeSingle,
      subjectID: this.shapeDR.id,
      version: -1,
      attribute: {
        key: AttributeKey.Translate,
        value: [newTranslate.x, newTranslate.y],
      },
    });
  }

  // Send events at the correct scope whcih update this shape's
  // translate properties. For live updates this is a little
  // complicated.
  applyTranslate(scope: Scope, point: Vector) {
    if (scope === Scope.Live) {
      // Throttle live events and mirror them to local
      this.throttledApplyTranslate(Scope.Live, point);
      this._applyTranslate(Scope.Local, point);
    } else {
      this._applyTranslate(scope, point);
    }
  }

  // Update transform of selection rect div
  updateTransform(camera: Camera) {
    const worldRect = this.shapeDR.selectionRect;
    const screenRect = new Rect(
      camera.convertWorldToScreen(worldRect.min),
      camera.convertWorldToScreen(worldRect.max)
    );
    this.el.style.left = `${screenRect.min.x}px`;
    this.el.style.top = `${screenRect.min.y}px`;
    this.el.style.width = `${screenRect.width}px`;
    this.el.style.height = `${screenRect.height}px`;

    for (const h of this.dragHandles) {
      h.applyTransform(camera);
    }
  }
}
